import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";
import { CountUp } from "@/components/common/CountUp";
import { SebbiaIsotipo } from "@/components/brand/SebbiaLogo";
import { EASE_SALIDA, useSinAnimacion } from "../Aparece";
import { EJEMPLO } from "../content";
import { DatosEjemplo, MarcoApp, Pill, clp } from "./MarcoApp";

const KPIS = [
  { label: "Recaudado", valor: 18420000, type: "currency", acento: "border-l-emerald-500" },
  { label: "Por cobrar", valor: 1260000, type: "currency", acento: "border-l-amber-500" },
  { label: "Ocupación", valor: 94, type: "percent", acento: "border-l-indigo-500" },
];

const FILAS = [
  { prop: "Depto. 1204 · Providencia", uf: "UF 18,5", estado: "Pagado", tono: "pagado" },
  { prop: "Oficina 32 · Santiago", uf: "UF 15,8", estado: "Atraso 4 días", tono: "atraso" },
  { prop: "Depto. 507 · Las Condes", uf: "UF 21,3", estado: "Pendiente", tono: "pendiente" },
  { prop: "Casa Los Aromos · Ñuñoa", uf: "UF 24,0", estado: "Pagado", tono: "pagado" },
];

// La cifra parte de cero y sube una sola vez al montar (el hero siempre está a la vista). Sin
// animación, el valor final desde el primer render: el conteo es un plus, nunca el dato.
function useDesdeCero(valor, sinAnimacion, retrasoMs = 450) {
  const [v, setV] = useState(sinAnimacion ? valor : 0);
  useEffect(() => {
    if (sinAnimacion) { setV(valor); return undefined; }
    const t = setTimeout(() => setV(valor), retrasoMs);
    return () => clearTimeout(t);
  }, [valor, sinAnimacion, retrasoMs]);
  return v;
}

const Kpi = ({ k, sinAnimacion }) => {
  const v = useDesdeCero(k.valor, sinAnimacion);
  return (
    <div className={`rounded-xl border-l-4 ${k.acento} bg-white px-3 py-2.5 ring-1 ring-inset ring-slate-200/60`}>
      <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 sm:text-[10px]">{k.label}</p>
      <p className="mt-0.5 font-display text-sm font-extrabold tabular-nums tracking-tight text-slate-900 sm:text-lg">
        <CountUp value={v} type={k.type} durationMs={900} />
      </p>
    </div>
  );
};

// El voucher de liquidación es el objeto protagonista: es lo que el propietario recibe de verdad.
const Voucher = () => (
  <div className="w-[15.5rem] rounded-2xl bg-white p-4 shadow-2xl shadow-indigo-950/20 ring-1 ring-slate-900/5 sm:w-72 sm:p-5">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Liquidación</p>
        <p className="font-display text-sm font-extrabold text-slate-900">{EJEMPLO.periodo}</p>
      </div>
      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-900">
        <SebbiaIsotipo className="h-[65%] text-gold-600" />
      </span>
    </div>
    <p className="mt-3 text-[11px] text-slate-500">Propietario · M. González</p>
    <dl className="mt-3 space-y-1.5 text-[11px] sm:text-xs">
      <div className="flex justify-between gap-2">
        <dt className="text-slate-600">Arriendo ({String(EJEMPLO.arriendoUf).replace(".", ",")} UF)</dt>
        <dd className="font-semibold tabular-nums text-slate-900">{clp(EJEMPLO.arriendo)}</dd>
      </div>
      <div className="flex justify-between gap-2">
        <dt className="text-slate-600">Honorarios {EJEMPLO.honorariosPct}%</dt>
        <dd className="font-semibold tabular-nums text-slate-900">−{clp(EJEMPLO.honorarios)}</dd>
      </div>
      <div className="flex justify-between gap-2">
        <dt className="text-slate-600">Mantención</dt>
        <dd className="font-semibold tabular-nums text-slate-900">−{clp(EJEMPLO.mantencion)}</dd>
      </div>
    </dl>
    <div className="mt-3 flex items-center justify-between rounded-xl bg-indigo-50 px-3 py-2">
      <span className="text-[11px] font-semibold text-indigo-900">Neto a transferir</span>
      <span className="font-display text-sm font-extrabold tabular-nums text-indigo-900">{clp(EJEMPLO.neto)}</span>
    </div>
    <p className="mt-2.5 flex items-center gap-1.5 text-[10px] font-semibold text-slate-400">
      <FileText className="h-3 w-3" /> PDF enviado al propietario
    </p>
  </div>
);

export const HeroComposicion = () => {
  const sinAnimacion = useSinAnimacion();
  const entra = (delay, extra = {}) =>
    sinAnimacion
      ? {}
      : {
          initial: { opacity: 0, y: 16, ...extra.initial },
          animate: { opacity: 1, y: 0, ...extra.animate },
          transition: { duration: 0.5, ease: EASE_SALIDA, delay },
        };

  // El voucher se monta sobre la franja INFERIOR del marco (última fila y rótulo), nunca sobre la
  // columna Estado de las primeras filas: taparía justo el dato que distingue a la tabla.
  return (
    <div className="relative pb-40 sm:pb-36 [@media(min-width:1024px)_and_(max-height:760px)]:pb-20" data-testid="hero-composicion">
      <motion.div className="sm:mr-8" {...entra(0.1)}>
        <MarcoApp titulo={`Resumen de período · ${EJEMPLO.periodo}`}>
          <div className="space-y-3 p-3 sm:p-4">
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {KPIS.map((k) => <Kpi key={k.label} k={k} sinAnimacion={sinAnimacion} />)}
            </div>
            <div className="overflow-hidden rounded-xl bg-white ring-1 ring-inset ring-slate-200/60">
              <div className="grid grid-cols-[1fr_auto_auto] gap-3 bg-slate-50 px-3 py-2 text-[9px] font-semibold uppercase tracking-wider text-slate-500 sm:text-[10px]">
                <span>Propiedad</span><span className="text-right">Arriendo</span><span className="w-20 text-right sm:w-24">Estado</span>
              </div>
              {FILAS.map((f) => (
                <div key={f.prop} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-t border-slate-100 px-3 py-2 text-[11px] sm:text-xs">
                  <span className="truncate font-medium text-slate-700">{f.prop}</span>
                  <span className="text-right font-semibold tabular-nums text-slate-900">{f.uf}</span>
                  <span className="w-20 text-right sm:w-24"><Pill tono={f.tono}>{f.estado}</Pill></span>
                </div>
              ))}
            </div>
            <DatosEjemplo />
          </div>
        </MarcoApp>
      </motion.div>

      <motion.div
        className="absolute bottom-0 right-1 sm:right-0 lg:-right-4"
        {...entra(0.35, { initial: { rotate: 0 }, animate: { rotate: 2.5 } })}
        style={sinAnimacion ? { rotate: 2.5 } : undefined}
      >
        <Voucher />
      </motion.div>
    </div>
  );
};
